import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ClipboardList,
  Save,
  RotateCcw,
  History,
  FileDown,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import toast from "react-hot-toast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  stampLetterheadOnFirstPage,
  downloadPdfBytes,
} from "../../utils/letterheadPdf";

// mm to push first-page content down below the letterhead header
const LETTERHEAD_OFFSET = 25;
import {
  DailyChecklistPayload,
  DailyChecklistRecord,
  SupplyStatus,
  WeeklyReportPayload,
  WeeklyReportRecord,
  YesNo,
  getDailyChecklistHistory,
  getWeeklyReportHistory,
  saveDailyChecklist,
  saveWeeklyReport,
} from "../../services/secretaryReports";
import { useAuth } from "../../hooks/useAuth";

type DailyChecklistFormState = DailyChecklistPayload;
type WeeklyReportFormState = WeeklyReportPayload;

const getTodayDate = () => new Date().toISOString().split("T")[0];

const getSecretaryNameFromUser = (
  user: ReturnType<typeof useAuth>["user"],
): string => {
  if (!user) {
    return "";
  }

  if (user.fullName?.trim()) {
    return user.fullName.trim();
  }

  return `${user.firstName || ""} ${user.lastName || ""}`.trim();
};

const formatYesNoValue = (value?: YesNo | null) => {
  if (value === "yes") {
    return "YES";
  }

  if (value === "no") {
    return "NO";
  }

  return "-";
};

const formatSupplyStatusValue = (value?: SupplyStatus | null) => {
  if (value === "ok") {
    return "OK";
  }

  if (value === "low") {
    return "LOW";
  }

  if (value === "critical") {
    return "CRITICAL";
  }

  return "-";
};

const formatTextValue = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return String(value);
};

const createSectionHeaderRow = (title: string) => [
  {
    content: title,
    colSpan: 2,
    styles: {
      fillColor: [220, 242, 190],
      textColor: [58, 95, 11],
      fontStyle: "bold",
    },
  },
];

const buildChecklistPdfRows = (
  item: DailyChecklistRecord,
  formatDate: (date: string) => string,
  formatDateTime: (date: string) => string,
) => [
  createSectionHeaderRow("Daily Client and Admissions"),
  ["Date", formatDate(item.date)],
  ["Number of walk-in inquiries", formatTextValue(item.walkInInquiries)],
  [
    "Number of registrations completed",
    formatTextValue(item.registrationsCompleted),
  ],
  [
    "Number of clients deferred/follow-up",
    formatTextValue(item.deferredFollowUp),
  ],
  ["Referral - WhatsApp", formatTextValue(item.referralWhatsapp)],
  ["Referral - TikTok", formatTextValue(item.referralTiktok)],
  ["Referral - Facebook", formatTextValue(item.referralFacebook)],
  ["Referral - Family/Friend", formatTextValue(item.referralFamilyFriend)],
  ["Referral - Billboard", formatTextValue(item.referralBillboard)],
  ["Referral - Flyers", formatTextValue(item.referralFlyers)],
  ["Referral - Other", formatTextValue(item.referralOther)],
  createSectionHeaderRow("Daily Communication"),
  [
    "WhatsApp inquiries received",
    formatTextValue(item.whatsappInquiriesReceived),
  ],
  [
    "Facebook inquiries/comments",
    formatTextValue(item.facebookInquiriesComments),
  ],
  ["TikTok comments handled", formatTextValue(item.tiktokCommentsHandled)],
  ["Emails (job applications)", formatTextValue(item.jobApplicationEmails)],
  createSectionHeaderRow("Daily Finance"),
  [
    "Payments recorded in system (KES)",
    formatTextValue(item.paymentsRecordedKes),
  ],
  ["Cash received (KES)", formatTextValue(item.cashReceivedKes)],
  ["Receipts issued", formatTextValue(item.receiptsIssued)],
  ["Petty cash spent today (KES)", formatTextValue(item.pettyCashSpentKes)],
  [
    "Remaining imprest balance (KES)",
    formatTextValue(item.remainingImprestBalanceKes),
  ],
  createSectionHeaderRow("Daily Operations"),
  ["Supplies checked", formatYesNoValue(item.suppliesChecked)],
  ["Supplies notes", formatTextValue(item.suppliesNotes)],
  ["Issues noted/escalated", formatYesNoValue(item.issuesEscalated)],
  ["Issues notes", formatTextValue(item.issuesNotes)],
  ["Front office orderly", formatYesNoValue(item.frontOfficeOrderly)],
  ["Front office notes", formatTextValue(item.frontOfficeNotes)],
  createSectionHeaderRow("Sign-Off"),
  ["Secretary name", formatTextValue(item.secretaryName)],
  ["Signature", formatTextValue(item.signature)],
  createSectionHeaderRow("Record Metadata"),
  ["Created at", formatDateTime(item.createdAt)],
  ["Last saved", formatDateTime(item.updatedAt)],
];

const buildWeeklyReportPdfRows = (
  item: WeeklyReportRecord,
  formatDate: (date: string) => string,
  formatDateTime: (date: string) => string,
  formatMoney: (value: number) => string,
) => [
  createSectionHeaderRow("Weekly Client Numbers"),
  ["Week ending", formatDate(item.weekEnding)],
  ["Total walk-in inquiries", formatTextValue(item.totalWalkInInquiries)],
  ["Total registrations", formatTextValue(item.totalRegistrations)],
  createSectionHeaderRow("Referral Sources"),
  ["WhatsApp", formatTextValue(item.referralWhatsapp)],
  ["TikTok", formatTextValue(item.referralTiktok)],
  ["Facebook", formatTextValue(item.referralFacebook)],
  ["Family / Friend Referral", formatTextValue(item.referralFamilyFriend)],
  ["Billboard", formatTextValue(item.referralBillboard)],
  ["Flyers", formatTextValue(item.referralFlyers)],
  ["Other (Specify)", formatTextValue(item.referralOther)],
  createSectionHeaderRow("Communication Summary"),
  ["WhatsApp inquiries", formatTextValue(item.whatsappInquiries)],
  ["Facebook inquiries", formatTextValue(item.facebookInquiries)],
  ["TikTok inquiries", formatTextValue(item.tiktokInquiries)],
  ["Emails (job-related)", formatTextValue(item.jobRelatedEmails)],
  createSectionHeaderRow("Financial Summary"),
  ["Total payments recorded", formatMoney(item.totalPaymentsRecordedKes || 0)],
  ["Total cash received", formatMoney(item.totalCashReceivedKes || 0)],
  ["Total petty cash used", formatMoney(item.totalPettyCashUsedKes || 0)],
  [
    "Remaining imprest balance",
    formatMoney(item.remainingImprestBalanceKes || 0),
  ],
  createSectionHeaderRow("Supplies & Operations"),
  ["Supplies status", formatSupplyStatusValue(item.suppliesStatus)],
  ["Items needed next week", formatTextValue(item.itemsNeededNextWeek)],
  createSectionHeaderRow("Issues & Observations"),
  ["Challenges faced", formatTextValue(item.challengesFaced)],
  ["System downtime incidents", formatTextValue(item.systemDowntimeIncidents)],
  ["Recommendations", formatTextValue(item.recommendations)],
  createSectionHeaderRow("Declaration"),
  ["Secretary name", formatTextValue(item.secretaryName)],
  ["Signature", formatTextValue(item.signature)],
  createSectionHeaderRow("Record Metadata"),
  ["Created at", formatDateTime(item.createdAt)],
  ["Last saved", formatDateTime(item.updatedAt)],
];

const initialFormState: DailyChecklistFormState = {
  walkInInquiries: "",
  registrationsCompleted: "",
  deferredFollowUp: "",
  referralWhatsapp: "",
  referralTiktok: "",
  referralFacebook: "",
  referralFamilyFriend: "",
  referralBillboard: "",
  referralFlyers: "",
  referralOther: "",
  whatsappInquiriesReceived: "",
  facebookInquiriesComments: "",
  tiktokCommentsHandled: "",
  jobApplicationEmails: "",
  paymentsRecordedKes: "",
  cashReceivedKes: "",
  receiptsIssued: "",
  pettyCashSpentKes: "",
  remainingImprestBalanceKes: "",
  suppliesChecked: "",
  suppliesNotes: "",
  issuesEscalated: "",
  issuesNotes: "",
  frontOfficeOrderly: "",
  frontOfficeNotes: "",
  secretaryName: "",
  date: getTodayDate(),
  signature: "",
};

const initialWeeklyFormState: WeeklyReportFormState = {
  totalWalkInInquiries: "",
  totalRegistrations: "",
  referralWhatsapp: "",
  referralTiktok: "",
  referralFacebook: "",
  referralFamilyFriend: "",
  referralBillboard: "",
  referralFlyers: "",
  referralOther: "",
  whatsappInquiries: "",
  facebookInquiries: "",
  tiktokInquiries: "",
  jobRelatedEmails: "",
  totalPaymentsRecordedKes: "",
  totalCashReceivedKes: "",
  totalPettyCashUsedKes: "",
  remainingImprestBalanceKes: "",
  suppliesStatus: "",
  itemsNeededNextWeek: "",
  challengesFaced: "",
  systemDowntimeIncidents: "",
  recommendations: "",
  secretaryName: "",
  weekEnding: getTodayDate(),
  signature: "",
};

const SecretaryReports: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [formData, setFormData] =
    useState<DailyChecklistFormState>(initialFormState);
  const [weeklyFormData, setWeeklyFormData] = useState<WeeklyReportFormState>(
    initialWeeklyFormState,
  );
  const [historySearch, setHistorySearch] = useState("");
  const [historyStartDate, setHistoryStartDate] = useState("");
  const [historyEndDate, setHistoryEndDate] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const [isExportingHistory, setIsExportingHistory] = useState(false);
  const [weeklyHistorySearch, setWeeklyHistorySearch] = useState("");
  const [weeklyHistoryStartDate, setWeeklyHistoryStartDate] = useState("");
  const [weeklyHistoryEndDate, setWeeklyHistoryEndDate] = useState("");
  const [weeklyHistoryPage, setWeeklyHistoryPage] = useState(1);
  const [isExportingWeeklyHistory, setIsExportingWeeklyHistory] =
    useState(false);
  const [expandedReportSection, setExpandedReportSection] = useState<
    "daily" | "weekly" | null
  >(null);

  const historyLimit = 10;
  const weeklyHistoryLimit = 10;

  const {
    data: historyData,
    isLoading: historyLoading,
    isFetching: historyFetching,
  } = useQuery({
    queryKey: [
      "secretary-daily-checklist-history",
      historySearch,
      historyStartDate,
      historyEndDate,
      historyPage,
    ],
    queryFn: () =>
      getDailyChecklistHistory({
        page: historyPage,
        limit: historyLimit,
        search: historySearch || undefined,
        startDate: historyStartDate || undefined,
        endDate: historyEndDate || undefined,
      }),
  });

  const {
    data: weeklyHistoryData,
    isLoading: weeklyHistoryLoading,
    isFetching: weeklyHistoryFetching,
  } = useQuery({
    queryKey: [
      "secretary-weekly-report-history",
      weeklyHistorySearch,
      weeklyHistoryStartDate,
      weeklyHistoryEndDate,
      weeklyHistoryPage,
    ],
    queryFn: () =>
      getWeeklyReportHistory({
        page: weeklyHistoryPage,
        limit: weeklyHistoryLimit,
        search: weeklyHistorySearch || undefined,
        startDate: weeklyHistoryStartDate || undefined,
        endDate: weeklyHistoryEndDate || undefined,
      }),
  });

  const saveChecklistMutation = useMutation({
    mutationFn: (payload: DailyChecklistPayload) => saveDailyChecklist(payload),
    onSuccess: (response) => {
      toast.success(response.message || "Daily checklist saved.");
      queryClient.invalidateQueries({
        queryKey: ["secretary-daily-checklist-history"],
      });
      setHistoryPage(1);
    },
    onError: (error: any) => {
      toast.error(
        error?.message || "Failed to save daily checklist. Please try again.",
      );
    },
  });

  const saveWeeklyReportMutation = useMutation({
    mutationFn: (payload: WeeklyReportPayload) => saveWeeklyReport(payload),
    onSuccess: (response) => {
      toast.success(response.message || "Weekly report saved.");
      queryClient.invalidateQueries({
        queryKey: ["secretary-weekly-report-history"],
      });
      setWeeklyHistoryPage(1);
    },
    onError: (error: any) => {
      toast.error(
        error?.message || "Failed to save weekly report. Please try again.",
      );
    },
  });

  const checklistHistory = historyData?.data || [];
  const historyPagination = historyData?.pagination;
  const weeklyReportHistory = weeklyHistoryData?.data || [];
  const weeklyHistoryPagination = weeklyHistoryData?.pagination;
  const secretaryName = getSecretaryNameFromUser(user);

  useEffect(() => {
    setFormData((prev) => {
      const nextDate = getTodayDate();
      const nextSecretaryName = secretaryName || prev.secretaryName;

      if (prev.secretaryName === nextSecretaryName && prev.date === nextDate) {
        return prev;
      }

      return {
        ...prev,
        secretaryName: nextSecretaryName,
        date: nextDate,
      };
    });
  }, [secretaryName]);

  useEffect(() => {
    setWeeklyFormData((prev) => {
      const nextWeekEnding = prev.weekEnding || getTodayDate();
      const nextSecretaryName = secretaryName || prev.secretaryName;

      if (
        prev.secretaryName === nextSecretaryName &&
        prev.weekEnding === nextWeekEnding
      ) {
        return prev;
      }

      return {
        ...prev,
        secretaryName: nextSecretaryName,
        weekEnding: nextWeekEnding,
      };
    });
  }, [secretaryName]);

  const handleChange = (
    field: keyof DailyChecklistFormState,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveChecklist = (event: React.FormEvent) => {
    event.preventDefault();

    const payload: DailyChecklistPayload = {
      ...formData,
      secretaryName: secretaryName || formData.secretaryName,
      date: getTodayDate(),
    };

    setFormData((prev) => ({
      ...prev,
      secretaryName: payload.secretaryName,
      date: payload.date,
    }));

    saveChecklistMutation.mutate(payload);
  };

  const handleResetForm = () => {
    setFormData({
      ...initialFormState,
      secretaryName,
      date: getTodayDate(),
    });
    toast.success("Daily checklist reset.");
  };

  const handleWeeklyChange = (
    field: keyof WeeklyReportFormState,
    value: string,
  ) => {
    setWeeklyFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveWeeklyReport = (event: React.FormEvent) => {
    event.preventDefault();

    const payload: WeeklyReportPayload = {
      ...weeklyFormData,
      secretaryName: secretaryName || weeklyFormData.secretaryName,
      weekEnding: weeklyFormData.weekEnding || getTodayDate(),
    };

    setWeeklyFormData((prev) => ({
      ...prev,
      secretaryName: payload.secretaryName,
      weekEnding: payload.weekEnding,
    }));

    saveWeeklyReportMutation.mutate(payload);
  };

  const handleResetWeeklyForm = () => {
    setWeeklyFormData({
      ...initialWeeklyFormState,
      secretaryName,
      weekEnding: getTodayDate(),
    });
    toast.success("Weekly report reset.");
  };

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const formatDateTime = (date: string) =>
    new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatMoney = (value: number) =>
    new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      maximumFractionDigits: 0,
    }).format(value || 0);

  const renderChecklistInPdf = (
    doc: jsPDF,
    item: DailyChecklistRecord,
    index: number,
    total: number,
    firstPageOffset: number = 0,
  ) => {
    if (index > 0) {
      doc.addPage();
    }

    const yOff = index === 0 ? firstPageOffset : 0;
    doc.setFontSize(12);
    doc.text(`Entry ${index + 1} of ${total}`, 14, 30 + yOff);

    autoTable(doc, {
      startY: 36 + yOff,
      head: [["Field", "Value"]],
      body: buildChecklistPdfRows(item, formatDate, formatDateTime),
      styles: { fontSize: 9, cellPadding: 3, overflow: "linebreak" },
      headStyles: { fillColor: [61, 122, 46] },
      columnStyles: {
        0: { cellWidth: 78, fontStyle: "bold" },
        1: { cellWidth: "auto" },
      },
    });
  };

  const exportSingleChecklistAsPdf = async (item: DailyChecklistRecord) => {
    const doc = new jsPDF({ orientation: "portrait" });
    const generatedAt = new Date().toLocaleString();

    doc.setFontSize(16);
    doc.text("Secretary Daily Checklist", 14, 14 + LETTERHEAD_OFFSET);
    doc.setFontSize(10);
    doc.text(`Generated: ${generatedAt}`, 14, 20 + LETTERHEAD_OFFSET);

    renderChecklistInPdf(doc, item, 0, 1, LETTERHEAD_OFFSET);

    const safeDate = item.date ? item.date.split("T")[0] : getTodayDate();
    const safeSecretary = (item.secretaryName || "secretary")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    const filename = `checklist-${safeDate}-${safeSecretary || "record"}.pdf`;

    try {
      const finalBytes = await stampLetterheadOnFirstPage(
        doc.output("arraybuffer") as ArrayBuffer,
      );
      downloadPdfBytes(finalBytes, filename);
    } catch {
      doc.save(filename);
    }
    toast.success("Checklist exported.");
  };

  const fetchHistoryEntriesForExport = async () => {
    const exportLimit = 100;
    const maxPages = 200;
    let page = 1;
    let totalPages = 1;
    const records: DailyChecklistRecord[] = [];

    while (page <= totalPages && page <= maxPages) {
      const response = await getDailyChecklistHistory({
        page,
        limit: exportLimit,
        search: historySearch || undefined,
        startDate: historyStartDate || undefined,
        endDate: historyEndDate || undefined,
      });

      records.push(...(response.data || []));

      totalPages = response.pagination?.totalPages || page;

      if (!response.pagination?.hasNext || !response.data?.length) {
        break;
      }

      page += 1;
    }

    return records;
  };

  const exportHistoryAsPdf = async () => {
    setIsExportingHistory(true);

    try {
      const exportRecords = await fetchHistoryEntriesForExport();

      if (!exportRecords.length) {
        toast.error("No checklist history to export for the selected filters.");
        return;
      }

      const doc = new jsPDF({ orientation: "portrait" });
      const generatedAt = new Date().toLocaleString();

      doc.setFontSize(16);
      doc.text("Secretary Daily Checklist History", 14, 14 + LETTERHEAD_OFFSET);
      doc.setFontSize(10);
      doc.text(`Generated: ${generatedAt}`, 14, 20 + LETTERHEAD_OFFSET);

      const rangeLabel =
        historyStartDate || historyEndDate
          ? `Range: ${historyStartDate || "Any"} to ${historyEndDate || "Any"}`
          : "Range: All dates";
      doc.text(rangeLabel, 14, 26 + LETTERHEAD_OFFSET);

      exportRecords.forEach((item: DailyChecklistRecord, index: number) => {
        renderChecklistInPdf(
          doc,
          item,
          index,
          exportRecords.length,
          LETTERHEAD_OFFSET,
        );
      });

      const fromLabel = historyStartDate || "all";
      const toLabel = historyEndDate || "all";
      const filename = `secretary-daily-checklist-history-${fromLabel}-to-${toLabel}.pdf`;
      const finalBytes = await stampLetterheadOnFirstPage(
        doc.output("arraybuffer") as ArrayBuffer,
      );
      downloadPdfBytes(finalBytes, filename);
      toast.success(
        `Exported ${exportRecords.length} checklist entr${exportRecords.length === 1 ? "y" : "ies"}.`,
      );
    } catch (error: any) {
      toast.error(error?.message || "Failed to export checklist history.");
    } finally {
      setIsExportingHistory(false);
    }
  };

  const handleClearHistoryFilters = () => {
    setHistoryStartDate("");
    setHistoryEndDate("");
    setHistorySearch("");
    setHistoryPage(1);
  };

  const isHistoryFilterActive =
    !!historyStartDate || !!historyEndDate || !!historySearch;

  const hasExportableHistory = !!(
    historyPagination?.totalItems || checklistHistory.length
  );

  const showNoHistoryToExport = () => {
    if (!hasExportableHistory) {
      toast.error("No checklist history to export.");
      return true;
    }

    return false;
  };

  const handleExportHistoryClick = async () => {
    if (showNoHistoryToExport()) {
      return;
    }

    await exportHistoryAsPdf();
  };

  const renderWeeklyReportInPdf = (
    doc: jsPDF,
    item: WeeklyReportRecord,
    index: number,
    total: number,
    firstPageOffset: number = 0,
  ) => {
    if (index > 0) {
      doc.addPage();
    }

    const yOff = index === 0 ? firstPageOffset : 0;
    doc.setFontSize(12);
    doc.text(`Entry ${index + 1} of ${total}`, 14, 30 + yOff);

    autoTable(doc, {
      startY: 36 + yOff,
      head: [["Field", "Value"]],
      body: buildWeeklyReportPdfRows(
        item,
        formatDate,
        formatDateTime,
        formatMoney,
      ),
      styles: { fontSize: 9, cellPadding: 3, overflow: "linebreak" },
      headStyles: { fillColor: [58, 95, 11] },
      columnStyles: {
        0: { cellWidth: 78, fontStyle: "bold" },
        1: { cellWidth: "auto" },
      },
    });
  };

  const exportSingleWeeklyReportAsPdf = async (item: WeeklyReportRecord) => {
    const doc = new jsPDF({ orientation: "portrait" });
    const generatedAt = new Date().toLocaleString();

    doc.setFontSize(16);
    doc.text("Secretary Weekly Report", 14, 14 + LETTERHEAD_OFFSET);
    doc.setFontSize(10);
    doc.text(`Generated: ${generatedAt}`, 14, 20 + LETTERHEAD_OFFSET);

    renderWeeklyReportInPdf(doc, item, 0, 1, LETTERHEAD_OFFSET);

    const safeDate = item.weekEnding
      ? item.weekEnding.split("T")[0]
      : getTodayDate();
    const safeSecretary = (item.secretaryName || "secretary")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    const filename = `weekly-report-${safeDate}-${safeSecretary || "record"}.pdf`;

    try {
      const finalBytes = await stampLetterheadOnFirstPage(
        doc.output("arraybuffer") as ArrayBuffer,
      );
      downloadPdfBytes(finalBytes, filename);
    } catch {
      doc.save(filename);
    }
    toast.success("Weekly report exported.");
  };

  const fetchWeeklyHistoryEntriesForExport = async () => {
    const exportLimit = 100;
    const maxPages = 200;
    let page = 1;
    let totalPages = 1;
    const records: WeeklyReportRecord[] = [];

    while (page <= totalPages && page <= maxPages) {
      const response = await getWeeklyReportHistory({
        page,
        limit: exportLimit,
        search: weeklyHistorySearch || undefined,
        startDate: weeklyHistoryStartDate || undefined,
        endDate: weeklyHistoryEndDate || undefined,
      });

      records.push(...(response.data || []));

      totalPages = response.pagination?.totalPages || page;

      if (!response.pagination?.hasNext || !response.data?.length) {
        break;
      }

      page += 1;
    }

    return records;
  };

  const exportWeeklyHistoryAsPdf = async () => {
    setIsExportingWeeklyHistory(true);

    try {
      const exportRecords = await fetchWeeklyHistoryEntriesForExport();

      if (!exportRecords.length) {
        toast.error("No weekly reports to export for the selected filters.");
        return;
      }

      const doc = new jsPDF({ orientation: "portrait" });
      const generatedAt = new Date().toLocaleString();

      doc.setFontSize(16);
      doc.text("Secretary Weekly Report History", 14, 14 + LETTERHEAD_OFFSET);
      doc.setFontSize(10);
      doc.text(`Generated: ${generatedAt}`, 14, 20 + LETTERHEAD_OFFSET);

      const rangeLabel =
        weeklyHistoryStartDate || weeklyHistoryEndDate
          ? `Range: ${weeklyHistoryStartDate || "Any"} to ${weeklyHistoryEndDate || "Any"}`
          : "Range: All weeks";
      doc.text(rangeLabel, 14, 26 + LETTERHEAD_OFFSET);

      exportRecords.forEach((item: WeeklyReportRecord, index: number) => {
        renderWeeklyReportInPdf(
          doc,
          item,
          index,
          exportRecords.length,
          LETTERHEAD_OFFSET,
        );
      });

      const fromLabel = weeklyHistoryStartDate || "all";
      const toLabel = weeklyHistoryEndDate || "all";
      const filename = `weekly-reports-history-${fromLabel}-to-${toLabel}.pdf`;
      const finalBytes = await stampLetterheadOnFirstPage(
        doc.output("arraybuffer") as ArrayBuffer,
      );
      downloadPdfBytes(finalBytes, filename);
      toast.success(
        `Exported ${exportRecords.length} weekly entr${exportRecords.length === 1 ? "y" : "ies"}.`,
      );
    } catch (error: any) {
      toast.error(error?.message || "Failed to export weekly report history.");
    } finally {
      setIsExportingWeeklyHistory(false);
    }
  };

  const handleClearWeeklyHistoryFilters = () => {
    setWeeklyHistoryStartDate("");
    setWeeklyHistoryEndDate("");
    setWeeklyHistorySearch("");
    setWeeklyHistoryPage(1);
  };

  const isWeeklyHistoryFilterActive =
    !!weeklyHistoryStartDate || !!weeklyHistoryEndDate || !!weeklyHistorySearch;

  const hasExportableWeeklyHistory = !!(
    weeklyHistoryPagination?.totalItems || weeklyReportHistory.length
  );

  const handleExportWeeklyHistoryClick = async () => {
    if (!hasExportableWeeklyHistory) {
      toast.error("No weekly reports to export.");
      return;
    }

    await exportWeeklyHistoryAsPdf();
  };

  const toggleReportSection = (section: "daily" | "weekly") => {
    setExpandedReportSection((prev) => (prev === section ? null : section));
  };

  const renderInput = (
    field: keyof DailyChecklistFormState,
    label: string,
    placeholder?: string,
    type: "text" | "number" | "date" = "text",
    readOnly = false,
  ) => (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-2">
        {label}
      </label>
      <input
        name={String(field)}
        type={type}
        value={formData[field] as string}
        onChange={(event) => {
          if (!readOnly) {
            handleChange(field, event.target.value);
          }
        }}
        placeholder={placeholder}
        readOnly={readOnly}
        autoComplete="off"
        data-bwignore="true"
        className={`w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
          readOnly ? "bg-slate-100 cursor-not-allowed" : "bg-white"
        }`}
      />
    </div>
  );

  const renderYesNoWithNotes = (
    field: keyof DailyChecklistFormState,
    notesField: keyof DailyChecklistFormState,
    label: string,
  ) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-sm font-semibold text-slate-700 mb-3">{label}</p>
      <div className="flex items-center gap-6 mb-3">
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="radio"
            name={field}
            checked={formData[field] === "yes"}
            onChange={() => handleChange(field, "yes")}
            autoComplete="off"
            data-bwignore="true"
            className="accent-blue-600"
          />
          Yes
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="radio"
            name={field}
            checked={formData[field] === "no"}
            onChange={() => handleChange(field, "no")}
            autoComplete="off"
            data-bwignore="true"
            className="accent-blue-600"
          />
          No
        </label>
      </div>
      <input
        name={String(notesField)}
        type="text"
        value={formData[notesField] as string}
        onChange={(event) => handleChange(notesField, event.target.value)}
        placeholder="Notes"
        autoComplete="off"
        data-bwignore="true"
        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
    </div>
  );

  const renderWeeklyInput = (
    field: keyof WeeklyReportFormState,
    label: string,
    placeholder?: string,
    type: "text" | "number" | "date" = "text",
    readOnly = false,
  ) => (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-2">
        {label}
      </label>
      <input
        name={String(field)}
        type={type}
        value={weeklyFormData[field] as string}
        onChange={(event) => {
          if (!readOnly) {
            handleWeeklyChange(field, event.target.value);
          }
        }}
        placeholder={placeholder}
        readOnly={readOnly}
        autoComplete="off"
        data-bwignore="true"
        className={`w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
          readOnly ? "bg-slate-100 cursor-not-allowed" : "bg-white"
        }`}
      />
    </div>
  );

  const renderWeeklyTextarea = (
    field: keyof WeeklyReportFormState,
    label: string,
    placeholder: string,
  ) => (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-2">
        {label}
      </label>
      <textarea
        name={String(field)}
        value={weeklyFormData[field] as string}
        onChange={(event) => handleWeeklyChange(field, event.target.value)}
        placeholder={placeholder}
        rows={3}
        autoComplete="off"
        data-bwignore="true"
        className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
    </div>
  );

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/40">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Secretary Reports
              </h1>
              <p className="text-gray-600 mt-1">Daily and Weekly Reports</p>
            </div>
          </div>
        </div>

        <section className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => toggleReportSection("daily")}
            className={`w-full rounded-2xl border px-5 py-4 flex items-center justify-between transition-all ${
              expandedReportSection === "daily"
                ? "border-blue-300 bg-blue-50 text-blue-900"
                : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
            }`}
            aria-expanded={expandedReportSection === "daily"}
          >
            <span className="inline-flex items-center gap-2 font-semibold">
              <ClipboardList className="w-4 h-4" />
              Daily Checklist
            </span>
            {expandedReportSection === "daily" ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>

          <button
            type="button"
            onClick={() => toggleReportSection("weekly")}
            className={`w-full rounded-2xl border px-5 py-4 flex items-center justify-between transition-all ${
              expandedReportSection === "weekly"
                ? "border-indigo-300 bg-indigo-50 text-indigo-900"
                : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
            }`}
            aria-expanded={expandedReportSection === "weekly"}
          >
            <span className="inline-flex items-center gap-2 font-semibold">
              <ClipboardList className="w-4 h-4" />
              Weekly Report
            </span>
            {expandedReportSection === "weekly" ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        </section>

        {expandedReportSection === "daily" && (
          <>
            <form
              onSubmit={handleSaveChecklist}
              autoComplete="off"
              data-bwignore="true"
              className="space-y-6 bg-white/90 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-xl border border-slate-200/60"
            >
              <div className="flex items-center gap-3 text-slate-800">
                <ClipboardList className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-bold">Secretary Daily Checklist</h2>
              </div>

              <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
                <h3 className="text-sm tracking-wide font-bold text-slate-700 uppercase mb-4">
                  Daily Client and Admissions
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderInput(
                    "walkInInquiries",
                    "Number of walk-in inquiries",
                    "0",
                    "number",
                  )}
                  {renderInput(
                    "registrationsCompleted",
                    "Number of registrations completed",
                    "0",
                    "number",
                  )}
                  {renderInput(
                    "deferredFollowUp",
                    "Number of clients deferred/follow-up",
                    "0",
                    "number",
                  )}
                </div>

                <h4 className="text-sm font-bold text-slate-700 mt-6 mb-3">
                  How Did the Student Hear About Us? (Registrations Completed)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {renderInput("referralWhatsapp", "WhatsApp", "0", "number")}
                  {renderInput("referralTiktok", "TikTok", "0", "number")}
                  {renderInput("referralFacebook", "Facebook", "0", "number")}
                  {renderInput(
                    "referralFamilyFriend",
                    "Family / Friend Referral",
                    "0",
                    "number",
                  )}
                  {renderInput("referralBillboard", "Billboard", "0", "number")}
                  {renderInput("referralFlyers", "Flyers", "0", "number")}
                  {renderInput(
                    "referralOther",
                    "Other (Specify)",
                    "Specify source",
                  )}
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
                <h3 className="text-sm tracking-wide font-bold text-slate-700 uppercase mb-4">
                  Daily Communication
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderInput(
                    "whatsappInquiriesReceived",
                    "WhatsApp inquiries received",
                    "0",
                    "number",
                  )}
                  {renderInput(
                    "facebookInquiriesComments",
                    "Facebook inquiries/comments",
                    "0",
                    "number",
                  )}
                  {renderInput(
                    "tiktokCommentsHandled",
                    "TikTok comments handled",
                    "0",
                    "number",
                  )}
                  {renderInput(
                    "jobApplicationEmails",
                    "Emails (job applications)",
                    "0",
                    "number",
                  )}
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
                <h3 className="text-sm tracking-wide font-bold text-slate-700 uppercase mb-4">
                  Daily Finance
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderInput(
                    "paymentsRecordedKes",
                    "Payments recorded in system (KES)",
                    "0",
                    "number",
                  )}
                  {renderInput(
                    "cashReceivedKes",
                    "Cash received (KES)",
                    "0",
                    "number",
                  )}
                  {renderInput(
                    "receiptsIssued",
                    "Receipts issued",
                    "0",
                    "number",
                  )}
                  {renderInput(
                    "pettyCashSpentKes",
                    "Petty cash spent today (KES)",
                    "0",
                    "number",
                  )}
                  {renderInput(
                    "remainingImprestBalanceKes",
                    "Remaining imprest balance (KES)",
                    "0",
                    "number",
                  )}
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
                <h3 className="text-sm tracking-wide font-bold text-slate-700 uppercase mb-4">
                  Daily Operations
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderYesNoWithNotes(
                    "suppliesChecked",
                    "suppliesNotes",
                    "Supplies checked",
                  )}
                  {renderYesNoWithNotes(
                    "issuesEscalated",
                    "issuesNotes",
                    "Issues noted/escalated",
                  )}
                  {renderYesNoWithNotes(
                    "frontOfficeOrderly",
                    "frontOfficeNotes",
                    "Front office orderly",
                  )}
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
                <h3 className="text-sm tracking-wide font-bold text-slate-700 uppercase mb-4">
                  Sign-Off
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderInput(
                    "secretaryName",
                    "Secretary Name",
                    undefined,
                    "text",
                    true,
                  )}
                  {renderInput("date", "Date", undefined, "date", true)}
                  {renderInput("signature", "Signature", "Type signature")}
                </div>
              </section>

              <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleResetForm}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-700 font-semibold hover:bg-slate-50 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
                <button
                  type="submit"
                  disabled={saveChecklistMutation.isPending}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  <Save className="w-4 h-4" />
                  {saveChecklistMutation.isPending
                    ? "Saving..."
                    : "Save Daily Checklist"}
                </button>
              </div>
            </form>

            <section className="mt-6 bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-slate-200/60">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
                <div className="flex items-center gap-3 text-slate-800">
                  <History className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-bold">Checklist History</h2>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative">
                    <input
                      name="dailyHistorySearch"
                      type="text"
                      value={historySearch}
                      onChange={(event) => {
                        setHistorySearch(event.target.value);
                        setHistoryPage(1);
                      }}
                      placeholder="Search by secretary name"
                      autoComplete="off"
                      data-bwignore="true"
                      className="pl-9 pr-3 py-2.5 rounded-xl border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <input
                    name="dailyHistoryStartDate"
                    type="date"
                    value={historyStartDate}
                    onChange={(event) => {
                      setHistoryStartDate(event.target.value);
                      setHistoryPage(1);
                    }}
                    className="px-3 py-2.5 rounded-xl border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    title="Start date"
                    autoComplete="off"
                    data-bwignore="true"
                  />
                  <input
                    name="dailyHistoryEndDate"
                    type="date"
                    value={historyEndDate}
                    onChange={(event) => {
                      setHistoryEndDate(event.target.value);
                      setHistoryPage(1);
                    }}
                    className="px-3 py-2.5 rounded-xl border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    title="End date"
                    autoComplete="off"
                    data-bwignore="true"
                  />
                  <button
                    type="button"
                    onClick={handleClearHistoryFilters}
                    disabled={!isHistoryFilterActive}
                    className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Clear Filters
                  </button>
                  <button
                    type="button"
                    onClick={handleExportHistoryClick}
                    disabled={isExportingHistory || !hasExportableHistory}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                  >
                    <FileDown className="w-4 h-4" />
                    {isExportingHistory ? "Exporting..." : "Export History PDF"}
                  </button>
                </div>
              </div>

              {historyLoading ? (
                <div className="py-12 text-center text-slate-600 font-medium">
                  Loading checklist history...
                </div>
              ) : checklistHistory.length === 0 ? (
                <div className="py-12 text-center text-slate-500 font-medium border border-dashed border-slate-300 rounded-xl">
                  No checklist history found yet.
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-100 text-slate-700">
                        <tr>
                          <th className="text-left px-4 py-3 font-semibold">
                            Date
                          </th>
                          <th className="text-left px-4 py-3 font-semibold">
                            Secretary
                          </th>
                          <th className="text-right px-4 py-3 font-semibold">
                            Walk-ins
                          </th>
                          <th className="text-right px-4 py-3 font-semibold">
                            Registrations
                          </th>
                          <th className="text-right px-4 py-3 font-semibold">
                            WhatsApp Inquiries
                          </th>
                          <th className="text-right px-4 py-3 font-semibold">
                            Payments
                          </th>
                          <th className="text-right px-4 py-3 font-semibold">
                            Cash Received
                          </th>
                          <th className="text-center px-4 py-3 font-semibold">
                            Front Office
                          </th>
                          <th className="text-left px-4 py-3 font-semibold">
                            Last Saved
                          </th>
                          <th className="text-center px-4 py-3 font-semibold">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {checklistHistory.map((item: DailyChecklistRecord) => (
                          <tr
                            key={item._id}
                            className="border-t border-slate-200 hover:bg-slate-50"
                          >
                            <td className="px-4 py-3 text-slate-700">
                              {formatDate(item.date)}
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-900">
                              {item.secretaryName || "-"}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-700">
                              {item.walkInInquiries || 0}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-700">
                              {item.registrationsCompleted || 0}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-700">
                              {item.whatsappInquiriesReceived || 0}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-700">
                              {formatMoney(item.paymentsRecordedKes || 0)}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-700">
                              {formatMoney(item.cashReceivedKes || 0)}
                            </td>
                            <td className="px-4 py-3 text-center text-slate-700 uppercase">
                              {item.frontOfficeOrderly || "-"}
                            </td>
                            <td className="px-4 py-3 text-slate-600">
                              {formatDateTime(item.updatedAt)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => exportSingleChecklistAsPdf(item)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-700 font-semibold hover:bg-emerald-100"
                              >
                                <FileDown className="w-3.5 h-3.5" />
                                Export
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
                    <span>
                      Showing {checklistHistory.length} of{" "}
                      {historyPagination?.totalItems || checklistHistory.length}{" "}
                      entries
                      {historyStartDate || historyEndDate
                        ? ` (date range: ${historyStartDate || "Any"} to ${historyEndDate || "Any"})`
                        : ""}
                      {historyFetching ? " (updating...)" : ""}
                    </span>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setHistoryPage((prev) => Math.max(prev - 1, 1))
                        }
                        disabled={!historyPagination?.hasPrev}
                        className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                      >
                        Previous
                      </button>
                      <span className="font-medium text-slate-700">
                        Page {historyPagination?.currentPage || 1} of{" "}
                        {historyPagination?.totalPages || 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => setHistoryPage((prev) => prev + 1)}
                        disabled={!historyPagination?.hasNext}
                        className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </section>
          </>
        )}

        {expandedReportSection === "weekly" && (
          <>
            <form
              onSubmit={handleSaveWeeklyReport}
              autoComplete="off"
              data-bwignore="true"
              className="mt-10 space-y-6 bg-white/90 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-xl border border-slate-200/60"
            >
              <div className="flex items-center gap-3 text-slate-800">
                <ClipboardList className="w-6 h-6 text-indigo-600" />
                <h2 className="text-xl font-bold">Weekly Secretary Report</h2>
              </div>

              <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
                <h3 className="text-sm tracking-wide font-bold text-slate-700 uppercase mb-4">
                  Weekly Client Numbers
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderWeeklyInput(
                    "totalWalkInInquiries",
                    "Total walk-in inquiries",
                    "0",
                    "number",
                  )}
                  {renderWeeklyInput(
                    "totalRegistrations",
                    "Total registrations",
                    "0",
                    "number",
                  )}
                </div>

                <h4 className="text-sm font-bold text-slate-700 mt-6 mb-3">
                  How Did the Student Hear About Us? (Total Registrations)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {renderWeeklyInput(
                    "referralWhatsapp",
                    "WhatsApp",
                    "0",
                    "number",
                  )}
                  {renderWeeklyInput("referralTiktok", "TikTok", "0", "number")}
                  {renderWeeklyInput(
                    "referralFacebook",
                    "Facebook",
                    "0",
                    "number",
                  )}
                  {renderWeeklyInput(
                    "referralFamilyFriend",
                    "Family / Friend Referral",
                    "0",
                    "number",
                  )}
                  {renderWeeklyInput(
                    "referralBillboard",
                    "Billboard",
                    "0",
                    "number",
                  )}
                  {renderWeeklyInput("referralFlyers", "Flyers", "0", "number")}
                  {renderWeeklyInput(
                    "referralOther",
                    "Other (Specify)",
                    "Specify source",
                  )}
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
                <h3 className="text-sm tracking-wide font-bold text-slate-700 uppercase mb-4">
                  Communication Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderWeeklyInput(
                    "whatsappInquiries",
                    "WhatsApp inquiries",
                    "0",
                    "number",
                  )}
                  {renderWeeklyInput(
                    "facebookInquiries",
                    "Facebook inquiries",
                    "0",
                    "number",
                  )}
                  {renderWeeklyInput(
                    "tiktokInquiries",
                    "TikTok inquiries",
                    "0",
                    "number",
                  )}
                  {renderWeeklyInput(
                    "jobRelatedEmails",
                    "Emails (job-related)",
                    "0",
                    "number",
                  )}
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
                <h3 className="text-sm tracking-wide font-bold text-slate-700 uppercase mb-4">
                  Financial Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {renderWeeklyInput(
                    "totalPaymentsRecordedKes",
                    "Total payments recorded (KES)",
                    "0",
                    "number",
                  )}
                  {renderWeeklyInput(
                    "totalCashReceivedKes",
                    "Total cash received (KES)",
                    "0",
                    "number",
                  )}
                  {renderWeeklyInput(
                    "totalPettyCashUsedKes",
                    "Total petty cash used (KES)",
                    "0",
                    "number",
                  )}
                  {renderWeeklyInput(
                    "remainingImprestBalanceKes",
                    "Remaining imprest balance (KES)",
                    "0",
                    "number",
                  )}
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
                <h3 className="text-sm tracking-wide font-bold text-slate-700 uppercase mb-4">
                  Supplies & Operations
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Supplies status
                    </label>
                    <select
                      name="suppliesStatus"
                      value={weeklyFormData.suppliesStatus}
                      onChange={(event) =>
                        handleWeeklyChange("suppliesStatus", event.target.value)
                      }
                      autoComplete="off"
                      data-bwignore="true"
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select status</option>
                      <option value="ok">OK</option>
                      <option value="low">Low</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  {renderWeeklyInput(
                    "itemsNeededNextWeek",
                    "Items needed next week",
                    "List required items",
                  )}
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
                <h3 className="text-sm tracking-wide font-bold text-slate-700 uppercase mb-4">
                  Issues & Observations
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderWeeklyTextarea(
                    "challengesFaced",
                    "Challenges faced",
                    "Document key challenges",
                  )}
                  {renderWeeklyTextarea(
                    "systemDowntimeIncidents",
                    "System downtime incidents",
                    "List incidents",
                  )}
                  {renderWeeklyTextarea(
                    "recommendations",
                    "Recommendations",
                    "Add recommendations",
                  )}
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
                <h3 className="text-sm tracking-wide font-bold text-slate-700 uppercase mb-4">
                  Declaration
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  I confirm the above information is accurate.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {renderWeeklyInput(
                    "secretaryName",
                    "Secretary Name",
                    undefined,
                    "text",
                    true,
                  )}
                  {renderWeeklyInput(
                    "weekEnding",
                    "Week Ending",
                    undefined,
                    "date",
                  )}
                  {renderWeeklyInput(
                    "signature",
                    "Signature",
                    "Type signature",
                  )}
                </div>
              </section>

              <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleResetWeeklyForm}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-700 font-semibold hover:bg-slate-50 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset Weekly Report
                </button>
                <button
                  type="submit"
                  disabled={saveWeeklyReportMutation.isPending}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-blue-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  <Save className="w-4 h-4" />
                  {saveWeeklyReportMutation.isPending
                    ? "Saving..."
                    : "Save Weekly Report"}
                </button>
              </div>
            </form>

            <section className="mt-6 bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-slate-200/60">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
                <div className="flex items-center gap-3 text-slate-800">
                  <History className="w-5 h-5 text-indigo-600" />
                  <h2 className="text-lg font-bold">Weekly Report History</h2>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative">
                    <input
                      name="weeklyHistorySearch"
                      type="text"
                      value={weeklyHistorySearch}
                      onChange={(event) => {
                        setWeeklyHistorySearch(event.target.value);
                        setWeeklyHistoryPage(1);
                      }}
                      placeholder="Search by secretary name"
                      autoComplete="off"
                      data-bwignore="true"
                      className="pl-9 pr-3 py-2.5 rounded-xl border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <input
                    name="weeklyHistoryStartDate"
                    type="date"
                    value={weeklyHistoryStartDate}
                    onChange={(event) => {
                      setWeeklyHistoryStartDate(event.target.value);
                      setWeeklyHistoryPage(1);
                    }}
                    className="px-3 py-2.5 rounded-xl border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    title="Start week ending"
                    autoComplete="off"
                    data-bwignore="true"
                  />
                  <input
                    name="weeklyHistoryEndDate"
                    type="date"
                    value={weeklyHistoryEndDate}
                    onChange={(event) => {
                      setWeeklyHistoryEndDate(event.target.value);
                      setWeeklyHistoryPage(1);
                    }}
                    className="px-3 py-2.5 rounded-xl border border-slate-300 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    title="End week ending"
                    autoComplete="off"
                    data-bwignore="true"
                  />
                  <button
                    type="button"
                    onClick={handleClearWeeklyHistoryFilters}
                    disabled={!isWeeklyHistoryFilterActive}
                    className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Clear Filters
                  </button>
                  <button
                    type="button"
                    onClick={handleExportWeeklyHistoryClick}
                    disabled={
                      isExportingWeeklyHistory || !hasExportableWeeklyHistory
                    }
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all"
                  >
                    <FileDown className="w-4 h-4" />
                    {isExportingWeeklyHistory
                      ? "Exporting..."
                      : "Export Weekly History PDF"}
                  </button>
                </div>
              </div>

              {weeklyHistoryLoading ? (
                <div className="py-12 text-center text-slate-600 font-medium">
                  Loading weekly report history...
                </div>
              ) : weeklyReportHistory.length === 0 ? (
                <div className="py-12 text-center text-slate-500 font-medium border border-dashed border-slate-300 rounded-xl">
                  No weekly report history found yet.
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-100 text-slate-700">
                        <tr>
                          <th className="text-left px-4 py-3 font-semibold">
                            Week Ending
                          </th>
                          <th className="text-left px-4 py-3 font-semibold">
                            Secretary
                          </th>
                          <th className="text-right px-4 py-3 font-semibold">
                            Walk-ins
                          </th>
                          <th className="text-right px-4 py-3 font-semibold">
                            Registrations
                          </th>
                          <th className="text-right px-4 py-3 font-semibold">
                            Payments
                          </th>
                          <th className="text-right px-4 py-3 font-semibold">
                            Cash Received
                          </th>
                          <th className="text-center px-4 py-3 font-semibold">
                            Supplies Status
                          </th>
                          <th className="text-left px-4 py-3 font-semibold">
                            Last Saved
                          </th>
                          <th className="text-center px-4 py-3 font-semibold">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {weeklyReportHistory.map((item: WeeklyReportRecord) => (
                          <tr
                            key={item._id}
                            className="border-t border-slate-200 hover:bg-slate-50"
                          >
                            <td className="px-4 py-3 text-slate-700">
                              {formatDate(item.weekEnding)}
                            </td>
                            <td className="px-4 py-3 font-medium text-slate-900">
                              {item.secretaryName || "-"}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-700">
                              {item.totalWalkInInquiries || 0}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-700">
                              {item.totalRegistrations || 0}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-700">
                              {formatMoney(item.totalPaymentsRecordedKes || 0)}
                            </td>
                            <td className="px-4 py-3 text-right text-slate-700">
                              {formatMoney(item.totalCashReceivedKes || 0)}
                            </td>
                            <td className="px-4 py-3 text-center text-slate-700 uppercase">
                              {formatSupplyStatusValue(item.suppliesStatus)}
                            </td>
                            <td className="px-4 py-3 text-slate-600">
                              {formatDateTime(item.updatedAt)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() =>
                                  exportSingleWeeklyReportAsPdf(item)
                                }
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-700 font-semibold hover:bg-emerald-100"
                              >
                                <FileDown className="w-3.5 h-3.5" />
                                Export
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
                    <span>
                      Showing {weeklyReportHistory.length} of{" "}
                      {weeklyHistoryPagination?.totalItems ||
                        weeklyReportHistory.length}{" "}
                      weekly entries
                      {weeklyHistoryStartDate || weeklyHistoryEndDate
                        ? ` (week range: ${weeklyHistoryStartDate || "Any"} to ${weeklyHistoryEndDate || "Any"})`
                        : ""}
                      {weeklyHistoryFetching ? " (updating...)" : ""}
                    </span>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setWeeklyHistoryPage((prev) => Math.max(prev - 1, 1))
                        }
                        disabled={!weeklyHistoryPagination?.hasPrev}
                        className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                      >
                        Previous
                      </button>
                      <span className="font-medium text-slate-700">
                        Page {weeklyHistoryPagination?.currentPage || 1} of{" "}
                        {weeklyHistoryPagination?.totalPages || 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => setWeeklyHistoryPage((prev) => prev + 1)}
                        disabled={!weeklyHistoryPagination?.hasNext}
                        className="px-3 py-1.5 rounded-lg border border-slate-300 bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default SecretaryReports;
